import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Inbox, MessageCircle } from "lucide-react";
import api from "@/lib/api";
import { Seo } from "@/components/Seo";

const fmtDate = (iso) => new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });
const campaign = (l) => [l.utm_source, l.utm_medium, l.utm_campaign].filter(Boolean).join(" / ") || (l.gclid ? "Google (gclid)" : l.fbclid ? "Meta (fbclid)" : "—");

export default function AdminLeads() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState(null);

  useEffect(() => {
    api.get("/auth/me")
      .then(() => api.get("/leads/trilocale"))
      .then((r) => setLeads(r.data))
      .catch(() => { localStorage.removeItem("cs_token"); navigate("/admin"); });
  }, [navigate]);

  const exportCsv = async () => {
    const r = await api.get("/leads/trilocale/export.csv", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = Object.assign(document.createElement("a"), { href: url, download: "lead-trilocale-paterno-12.csv" });
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <main className="min-h-screen bg-[#0A0A0C] text-zinc-100 grain-overlay" data-testid="admin-leads-page">
      <Seo title="Lead Trilocale — Area Riservata Capital Stone" description="Lead landing trilocale" path="/admin/lead-trilocale" noindex />
      <header className="border-b border-amber-500/10 bg-[#0D0D10]/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="px-4 sm:px-8 max-w-7xl mx-auto flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Capital Stone" className="h-9 w-auto" />
            <span className="text-zinc-500 text-[10px] tracking-[0.3em] uppercase">Lead Trilocale</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" data-testid="leads-back-link" className="flex items-center gap-2 text-xs text-zinc-500 hover:text-[#D4AF37] transition-colors duration-300">
              <ArrowLeft size={13} /> Gestionale
            </Link>
            <button
              data-testid="leads-export-csv"
              onClick={exportCsv}
              disabled={!leads?.length}
              className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-[#D4AF37] border border-amber-500/40 px-3 py-2 hover:bg-amber-500/10 disabled:opacity-40 transition-colors duration-300"
            >
              <Download size={13} /> Esporta CSV
            </button>
          </div>
        </div>
      </header>

      <div className="px-4 sm:px-8 max-w-7xl mx-auto py-10">
        <div className="flex items-end justify-between gap-4">
          <div>
            <p className="text-xs tracking-[0.35em] uppercase font-mono text-amber-400/90">Landing /trilocale-paterno-12</p>
            <h1 className="font-display text-3xl sm:text-4xl text-zinc-100 mt-2">Richieste di visita</h1>
          </div>
          <p className="font-display text-3xl text-gold" data-testid="leads-count">{leads?.length ?? "…"}</p>
        </div>

        {leads && leads.length === 0 && (
          <div className="border border-white/10 bg-[#121216] p-12 text-center mt-10 text-zinc-500" data-testid="leads-empty">
            <Inbox size={28} className="mx-auto mb-3" /> Nessun lead ricevuto finora.
          </div>
        )}

        {leads && leads.length > 0 && (
          <div className="overflow-x-auto border border-white/10 bg-[#121216] mt-10">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 border-b border-white/10">
                <tr>
                  {["Data", "Tipo", "Nome", "Telefono", "Email", "Visita", "Note", "Campagna", "Email inviata"].map((h) => (
                    <th key={h} className="text-left font-normal px-4 py-3 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-b border-white/5 hover:bg-white/[0.02] align-top" data-testid="lead-row">
                    <td className="px-4 py-3 whitespace-nowrap text-zinc-400 font-mono text-xs">{fmtDate(l.created_at)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-zinc-300">{l.tipo === "prossime_disponibilita" ? "Prossime disponibilità" : "Visita"}</td>
                    <td className="px-4 py-3 text-zinc-100 font-medium">{l.name}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <a href={`https://wa.me/${l.phone.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-zinc-300 hover:text-[#25D366]">
                        <MessageCircle size={13} /> {l.phone}
                      </a>
                    </td>
                    <td className="px-4 py-3"><a href={`mailto:${l.email}`} className="text-zinc-300 hover:text-[#D4AF37]">{l.email}</a></td>
                    <td className="px-4 py-3 whitespace-nowrap text-zinc-300">{l.visit_when}</td>
                    <td className="px-4 py-3 text-zinc-400 max-w-xs">{l.notes || "—"}</td>
                    <td className="px-4 py-3 text-zinc-400 text-xs">{campaign(l)}</td>
                    <td className="px-4 py-3 text-xs">{l.email_notified ? <span className="text-emerald-400">Sì</span> : <span className="text-zinc-500">No</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </main>
  );
}
